/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/lib/planAccess.ts
 * DESCRIPTION: Shared subscription normalization + paid-access logic.
 *              Single source of truth for "does this user have a real,
 *              non-default (non-Free) active subscription?".
 *
 * WHY:
 * Clerk Billing hooks live behind the experimental entry point
 * (@clerk/react/experimental) and their object shapes may evolve.
 * This module normalizes defensively and errs SAFE: unknown shapes
 * are treated as NOT paid (blocks free-riders, never grants silently).
 *
 * DECISION ORDER for isPaid:
 * 1. Cross-check plan id against the default plan (if plans list known)
 * 2. plan.isDefault boolean (authoritative when present)
 * 3. plan.amount > 0 (free plan = 0; 100% coupon users still pass,
 *    because amount is the plan's list price, not the paid price)
 * 4. Unknown → false (safe direction)
 *
 * NOTE: Experimental API risk is accepted for MVP. Pin @clerk/react
 * and review Clerk changelogs before upgrading.
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 1.0.0
 * @created 2026-09-24
 * ─────────────────────────────────────────────────────────────────────
 */

export interface NormalizedSubscription {
  exists: boolean
  isPaid: boolean
  status: string | null
  planName: string | null
  planId: string | null
  periodEnd: string | null
}

const ACTIVE_LIKE = ['active', 'trialing']

/**
 * Normalize a Clerk experimental subscription object into a stable shape.
 */
export const normalizeSubscription = (sub: any): NormalizedSubscription => {
  if (!sub || typeof sub !== 'object') {
    return { exists: false, isPaid: false, status: null, planName: null, planId: null, periodEnd: null }
  }

  if (import.meta.env?.DEV) {
    // Shape audit log — paste this output once per Clerk upgrade to re-validate fields
    console.debug('[planAccess] subscription shape:', sub)
  }

  const plan: any = sub.plan || {}
  const status = String(sub.status || '')
  const activeLike = ACTIVE_LIKE.includes(status)
  const amount = typeof plan.amount === 'number' ? plan.amount : null
  const periodEnd = sub.periodEnd ?? sub.period_end ?? sub.activeUntil ?? null

  let isPaid = false
  if (activeLike && plan.id) {
    if (typeof plan.isDefault === 'boolean') {
      isPaid = plan.isDefault === false
    } else if (amount !== null) {
      isPaid = amount > 0
    } else {
      isPaid = false // unknown shape → safe direction
    }
  }

  return {
    exists: true,
    isPaid,
    status: status || null,
    planName: plan.name ? String(plan.name) : null,
    planId: plan.id ? String(plan.id) : null,
    periodEnd: periodEnd ? String(periodEnd) : null,
  }
}

/**
 * Convenience: unwrap whatever the experimental hook returns.
 * Handles both { subscription } wrappers and bare subscription objects.
 */
export const unwrapSubscriptionHook = (hook: any): { subscription: any; isLoading: boolean; isError: boolean } => {
  if (!hook) return { subscription: null, isLoading: false, isError: false }
  if (hook.subscription !== undefined || hook.isLoading !== undefined || hook.isError !== undefined) {
    return {
      subscription: hook.subscription ?? null,
      isLoading: !!hook.isLoading,
      isError: !!hook.isError,
    }
  }
  // Bare subscription object (has id/status)
  if (hook.id || hook.status) return { subscription: hook, isLoading: false, isError: false }
  return { subscription: null, isLoading: false, isError: false }
}
